import React, { useEffect, useMemo } from 'react';

import { Field, FieldProps } from '@rjsf/utils';

import { ButtonWithIcon, Column, Group, Input, Row, Text } from 'components';
import { useRJSFormContext } from './RJSForm';
import { useExecutionId } from 'pages/app/task/hooks/useExecutionId';
import { useGeneratePublicUrl } from 'domain/swr/useUserAccess';
import cn from 'classnames';

import './ForwardControl.scss';
import { useConfirmationModal } from 'components/ConfirmationModal';
import { useDispatch } from 'react-redux';
import { NewSection, SettingsAutomation, UserPlus } from 'tabler-icons-react';
import { Autocomplete, Option } from 'components/Autocomplete';
import { useUserFetcher } from 'domain/swr';

const extractKey = (id: string) => {
  const parts = id.split('|');
  return parts.slice(1, parts.length - 1);
};

const goDeep = (obj: any, path: string[], key?: string) => {
  let current = obj;
  for (const p of path) {
    if (!current[p]) {
      return null;
    }
    current = current[p];
  }
  return key ? current[key] : current;
};

export const PublicEmailProvider: Field = (props: FieldProps) => {
  const [publicUrl, loading, generatePublicUrl] = useGeneratePublicUrl();
  const { formData } = useRJSFormContext();
  const { executionId } = useExecutionId();

  const theKey = useMemo(
    () => extractKey(props.idSchema.$id),
    [props.idSchema.$id]
  );
  const parent = useMemo(() => goDeep(formData, theKey), [formData, theKey]);

  const email = useMemo(
    () => goDeep(formData, theKey, 'multiEmail'),
    [formData, theKey]
  );

  // Fix parent
  useEffect(() => {
    if (parent && !email) {
      // Check if parent is an object, with email and name. If so, extract them.
      if (
        typeof parent === 'object' &&
        parent.email &&
        parent.name &&
        parent.name != undefined &&
        parent.email != undefined
      ) {
        props.onChange(`${parent.name} <${parent.email}>`.trim());
      }
    }
  }, []);

  useEffect(() => {
    if (email) {
      try {
        generatePublicUrl({
          emails: email,
          name: '',
          executionId,
        });
      } catch (error) {
        console.error('Error generating public URL:', error);
      }
    }
  }, [email]);

  const fromUserMap = (userMap: { email: string; name: string }) => {
    return `${userMap.name} <${userMap.email}>`;
  };

  const toUserMap = (userEntry: string) => {
    if (userEntry.includes('<')) {
      const email = userEntry.split('<')[1].replace('>', '').trim();
      const name = userEntry.split('<')[0].trim();
      return { email, name };
    }
    return { email: userEntry, name: userEntry.split('@')[0].trim() };
  };

  const userList: string[] = email ? (email ?? '').split(',') : [];
  const userListMap = userList.map((u) => toUserMap(u));

  const [addUser, doClose] = useConfirmationModal({
    title: 'Add User',
    message: (
      <AddUserModal
        onAdd={(emailData: string) => {
          if (!emailData || !emailData.includes('@')) {
            return;
          }

          // Add user logic here
          const existing = email ?? '';

          if (existing) {
            props.onChange(existing + ', ' + emailData);
          } else {
            props.onChange(emailData);
          }
          doClose();
        }}
      />
    ),
    onConfirm: () => {
      // do nothing
    },
    buttons: <></>,
  });

  const removeUser = (email: string) => {
    const newList = userListMap
      .filter((u) => u.email !== email)
      .map((u) => fromUserMap(u));
    props.onChange(newList.join(', '));
  };
  return (
    <Group
      className={cn('rjsf-forward-control', { 'opacity-50': loading })}
      orientation='column'
    >
      <Row className='forward-control-options w-full flex justify-start items-start text-black'>
        <Text className='forward-control-title w-full p-2 my-2'>
          Add the users to whom you want to forward the task for review.
        </Text>

        <Text className='forward-control-title bg-gray-200 w-full p-1 text-black flex flex-wrap flex-row justify-start'>
          {userListMap.length > 0 ? (
            userListMap.map((userEntry, _) => (
              <Text
                key={userEntry.email}
                className='forward-control-title p-1 my-2 text-sm text-black whitespace-nowrap '
              >
                {userEntry.name}{' '}
                <span className='text-xs'>&lt;{userEntry.email}&gt;</span>{' '}
                <span
                  onClick={() => removeUser(userEntry.email)}
                  className='cursor-pointer text-red-600 hover:text-red-800 text-md'
                >
                  &times;
                </span>
              </Text>
            ))
          ) : (
            <Text className='forward-control-title w-full p-2 my-2'></Text>
          )}
        </Text>
        <Column className='flex flex-row w-full justify-start items-start'>
          <ButtonWithIcon
            icon={<UserPlus />}
            onClick={(e) => {
              e.preventDefault(); // This seems to fire the form submit
              addUser();
            }}
            className='p-0 m-0 text-sm px-1 mt-2'
          >
            Add User
          </ButtonWithIcon>
        </Column>

        <Text className='forward-control-title w-full p-2 my-2 h-8 truncate overflow-ellipsis'>
          Public URL:
        </Text>
        <Input
          className='forward-control-title bg-gray-200 w-full p-1 my-0 h-10 truncate overflow-ellipsis leading-2 pt-2 text-black'
          value={publicUrl ?? ''}
          readOnly
        />
      </Row>
    </Group>
  );
};

const AddUserModal = ({ onAdd }: { onAdd: (emailData: string) => void }) => {
  const [name, setName] = React.useState<string>('');
  const [email, setEmail] = React.useState<string>('');

  const doOnPick = (selected: Option | undefined) => {
    if (selected) {
      setEmail(selected.value);
    }
  };

  const userFetcher = useUserFetcher();

  const onUserFetch = (q: string) => {
    setEmail(q);
    return userFetcher(undefined, q).then(
      (res) =>
        res.content?.map((user) => ({
          label: user.username,
          value: user.username,
        })) || []
    );
  };

  return (
    <Group orientation='column' placement='start'>
      <div>
        Please add the email for the reviewer.
        {/*If the email is already known,
        the name will be filled automatically.*/}
      </div>
      <Column className='justify-between flex-1 flex-grow gap-1 w-full'>
        <Autocomplete
          className='w-full'
          placeholder='Email of the reviewer'
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fetcher={onUserFetch}
          onPick={doOnPick}
        />
      </Column>
      <Column className='justify-between flex-1 flex-grow gap-1 w-full'>
        <Input
          className='w-full'
          placeholder='Name of the reviewer'
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Column>

      <ButtonWithIcon
        icon={<NewSection />}
        onClick={(e) => {
          e.preventDefault(); // This seems to fire the form submit
          onAdd(`${name} <${email}>`.trim());
        }}
      >
        Add User
      </ButtonWithIcon>
    </Group>
  );
};
